import { useEffect, useState } from 'react';
import { Icon } from '../../components/ui/Icon';
import type { StageLite } from './dealsApi';

/**
 * Popover "Chiudi": in che modo la trattativa si è fermata e perché.
 *
 * I modi arrivano dalla pipeline (gli step con esito PERSO) e restano distinti
 * apposta: "non raggiungibile" si richiama fra un mese, "non ammissibile" si
 * recupera sistemando i requisiti, "non ammessa" è una bocciatura del bando.
 * Il motivo è obbligatorio, altrimenti fra sei mesi nessuno ricorda perché
 * quella riga è ferma lì.
 */
const HINT: Record<string, string> = {
  'Non raggiungibile': 'Non risponde, numero sbagliato, nessun riscontro',
  'Non interessato': 'Ha detto no',
  'Non ammissibile': 'Gli mancano i requisiti del bando',
  'Non ammessa': 'Domanda presentata, esito negativo',
  'Non rinnova': 'Non rinnova il contratto',
  Rimandata: 'Se ne riparla più avanti',
  Rifiutata: 'Ha detto no alla proposta',
};

export function ClosePopover({ title, stages, pending, defaultStageId, onConfirm, onClose }: {
  /** Cosa si sta chiudendo: il nome dell'azienda. */
  title: string;
  /** Gli step con esito PERSO della pipeline. */
  stages: StageLite[];
  pending?: boolean;
  defaultStageId?: string;
  onConfirm: (v: { stageId: string; stageName: string; lostReason: string }) => void;
  onClose: () => void;
}) {
  const [stageId, setStageId] = useState(defaultStageId ?? stages[0]?.id ?? '');
  const [reason, setReason] = useState('');
  const stage = stages.find((s) => s.id === stageId);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = () => {
    if (pending || !reason.trim() || !stage) return;
    onConfirm({ stageId: stage.id, stageName: stage.name, lostReason: reason.trim() });
  };

  return (
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        className="dropdown adm-pop right-0 mt-1 z-[61]"
        role="dialog"
        aria-label="Chiudi la trattativa"
        style={{ width: 'min(400px, calc(100vw - 32px))' }}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) submit(); }}
      >
        <div className="adm-pop-hd">
          <span className="t-micro">Chiudi</span>
          <span className="font-semibold truncate">{title}</span>
        </div>
        <div className="adm-pop-grid">
          <label className="field-label">Come</label>
          <select className="inp inp-sm" value={stageId} onChange={(e) => setStageId(e.target.value)} autoFocus>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <label className="field-label">Perché</label>
          <input
            className="inp inp-sm"
            placeholder={(stage && HINT[stage.name]) ?? 'In due parole'}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); submit(); } }}
          />
        </div>
        <div className="px-4 pb-3 text-[12px] mute" style={{ lineHeight: 1.45 }}>
          Esce dal potenziale e resta nella pill Chiuse. Si può sempre rimettere in corso.
        </div>
        <div className="adm-pop-ft">
          <button className="btn btn-sm btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-sm" disabled={pending || !reason.trim() || !stage} onClick={submit}>
            <Icon name="x" size={10} /> Chiudi
          </button>
        </div>
      </div>
    </>
  );
}
